<script setup>
import { ref, computed } from 'vue';
import { register, checkEmailExists, checkUsernameExists, generateUsernameSuggestions } from '../db/auth.js';
import { useRouter } from 'vue-router';

const router = useRouter();
const username = ref('');
const password = ref('');
const full_name = ref('');
const email = ref('');
const role_id = ref(null);
const loading = ref(false);
const error = ref('');
const errors = ref({ full_name: '', username: '', email: '', password: '' });
const avatar_url = ref('');
const showPassword = ref(false);
const usernameSuggestions = ref([]);

// Modal state
const showModal = ref(false);
const modalType = ref('error'); // 'error' | 'success'
const modalTitle = ref('');
const modalMessage = ref('');

const defaultAvatar = computed(() => {
  const name = (full_name.value || username.value || 'User').trim();
  const initials = name.split(/[\s_]+/).filter(Boolean).slice(0, 2).map(s => s[0].toUpperCase()).join('') || 'U';
  const svg = `<?xml version="1.0" encoding="UTF-8"?><svg xmlns="http://www.w3.org/2000/svg" width="128" height="128"><rect width="100%" height="100%" rx="64" fill="#E6F7F5"/><text x="50%" y="54%" dominant-baseline="middle" text-anchor="middle" font-family="Outfit, sans-serif" font-size="56" fill="#00C2A8">${initials}</text></svg>`;
  return `data:image/svg+xml;base64,${btoa(svg)}`;
});

const passwordStrength = computed(() => {
  const p = password.value;
  if (!p) return { level: 0, label: '', color: '' };
  let score = 0;
  if (p.length >= 10) score++;
  if (/[A-Z]/.test(p)) score++;
  if (/[a-z]/.test(p)) score++;
  if (/[0-9]/.test(p)) score++;
  if (/[^A-Za-z0-9]/.test(p)) score++;

  if (score <= 2) return { level: score, label: 'Débil', color: '#ff6b6b' };
  if (score === 3) return { level: score, label: 'Media', color: '#ffa94d' };
  if (score === 4) return { level: score, label: 'Buena', color: '#51cf66' };
  return { level: score, label: 'Excelente', color: '#00C2A8' };
});

function showModalDialog(type, title, message) {
  modalType.value = type;
  modalTitle.value = title;
  modalMessage.value = message;
  showModal.value = true;
}

function closeModal() {
  showModal.value = false;

  // Solo redirigir a la página de activación si el registro fue exitoso
  if (modalType.value === 'success') {
    router.push({
      path: '/activate',
      query: {
        user: username.value.trim(),
        email: email.value.trim()
      }
    });
  }
  // Si es error, simplemente cerrar el modal y dejar al usuario corregir
}


async function submit() {
  error.value = '';
  usernameSuggestions.value = [];

  if (!validateAll()) {
    error.value = 'Revisa los campos marcados en rojo.';
    showModalDialog('error', 'Formulario incompleto', error.value);
    return;
  }

  try {
    loading.value = true;
    console.log('Iniciando registro...', {
      username: username.value.trim(),
      full_name: full_name.value,
      email: email.value,
      has_avatar: !!avatar_url.value
    });

    const res = await register({
      username: username.value.trim(),
      password: password.value,
      full_name: full_name.value,
      email: email.value,
      role_id: role_id.value,
      avatar_url: avatar_url.value || defaultAvatar.value
    });

    console.log('Respuesta de registro:', res);
    loading.value = false;

    if (!res?.ok) {
      const errorMsg = (res && res.message) || 'No se pudo registrar';
      console.error('Error en registro:', errorMsg, res);
      error.value = errorMsg;

      // Si hay sugerencias de usuario, mostrarlas
      if (res.suggestions && res.suggestions.length > 0) {
        usernameSuggestions.value = res.suggestions;
        errors.value.username = 'Este usuario ya está en uso';
        showModalDialog('error', 'Usuario no disponible', `El usuario "${username.value}" ya está registrado. Prueba con alguno de estos:\n\n• ${res.suggestions.join('\n• ')}`);
      } else {
        showModalDialog('error', 'Registro fallido', errorMsg);
      }
      return;
    }

    console.log('Registro exitoso, redirigiendo a activación...');

    // Mostrar información sobre el envío de email
    if (res.mail_ok === false && res.mail_error) {
      console.error('❌ Email no enviado:', res.mail_error);
      showModalDialog('error', 'Email no enviado', `El usuario fue creado exitosamente pero no se pudo enviar el email de activación.\n\nError: ${res.mail_error}\n\nCódigo de activación: ${res.activation_code}\n\nPor favor, anota este código para activar tu cuenta.`);
      return; // No redirigir automáticamente
    } else if (res.mail_ok) {
      console.log('✅ Email enviado exitosamente');
      showModalDialog('success', 'Registro exitoso', `Se ha enviado un código de activación a ${email.value}. Por favor revisa tu correo (también la carpeta de spam).\n\nHaz clic en OK para ir a la página de activación.`);
    } else {
      console.warn('⚠️ Estado de email desconocido');
      showModalDialog('success', 'Registro exitoso', `Usuario creado. Código de activación: ${res.activation_code}`);
    }
  } catch (e) {
    loading.value = false;
    console.error('Excepción en registro:', e);
    const msg = e?.message || 'Ocurrió un error inesperado durante el registro.';
    error.value = msg;
    showModalDialog('error', 'Error en registro', msg);
  }
}

function selectSuggestion(suggestion) {
  username.value = suggestion;
  usernameSuggestions.value = [];
  errors.value.username = '';
}

function validateAll() {
  const fields = ['full_name', 'username', 'email', 'password'];
  let ok = true;
  fields.forEach((f) => { if (!validateField(f)) ok = false; });
  return ok;
}

function validateField(field) {
  const val =
    field === 'full_name' ? full_name.value :
      field === 'username' ? username.value :
        field === 'email' ? email.value :
          password.value;
  const v = (val || '').trim();
  if (!v) { errors.value[field] = 'Campo obligatorio'; return false; }
  if (field === 'email') {
    const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
    if (!ok) { errors.value.email = 'Formato de email inválido'; return false; }
  }
  if (field === 'password') {
    const msg = validatePassword(v);
    if (msg) { errors.value.password = msg; return false; }
  }
  errors.value[field] = '';
  return true;
}

function validatePassword(v) {
  const rules = [
    { ok: v.length >= 10, label: 'mínimo 10 caracteres' },
    { ok: /[A-Z]/.test(v), label: 'una mayúscula' },
    { ok: /[a-z]/.test(v), label: 'una minúscula' },
    { ok: /[0-9]/.test(v), label: 'un número' },
    { ok: /[^A-Za-z0-9]/.test(v), label: 'un símbolo' }
  ];
  const missing = rules.filter(r => !r.ok).map(r => r.label);
  return missing.length ? `Debe incluir ${missing.join(', ')}.` : '';
}

function clearFieldError(field) { errors.value[field] = ''; }

function onAvatarChange(e) {
  const file = e.target.files?.[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = () => { avatar_url.value = String(reader.result || ''); };
  reader.readAsDataURL(file);
}
</script>

<template>
  <main class="register-container">
    <!-- Custom Modal -->
    <Transition name="modal">
      <div v-if="showModal" class="modal-overlay" @click="closeModal">
        <div class="modal-container" @click.stop>
          <div class="modal-icon" :class="modalType">
            <span class="material-symbols-rounded">
              {{ modalType === 'error' ? 'cancel' : 'check_circle' }}
            </span>
          </div>
          <h3 class="modal-title">{{ modalTitle }}</h3>
          <p class="modal-message">{{ modalMessage }}</p>
          <button class="modal-btn" @click="closeModal">OK</button>
        </div>
      </div>
    </Transition>
    <div class="register-wrapper">
      <!-- Left Side - Form -->
      <div class="form-side">
        <div class="form-content">
          <div class="header">
            <router-link to="/" class="back-to-landing">
              <span class="material-symbols-rounded">arrow_back</span>
              Volver al inicio
            </router-link>
            <h1 class="welcome-text">Bienvenido a</h1>
            <h2 class="logo-text" style="font-size: 3.5rem; margin-bottom: 0.5rem;">Sushito</h2>
          </div>

          <form @submit.prevent="submit" class="register-form">
            <Transition name="slide-fade">
              <div v-if="error" class="error-message">
                <span class="material-symbols-rounded">error</span>
                <span>{{ error }}</span>
              </div>
            </Transition>

            <!-- Avatar Upload -->
            <div class="avatar-upload">
              <div class="avatar-container">
                <img :src="avatar_url || defaultAvatar" alt="Avatar" class="avatar-preview" />
                <label class="upload-label">
                  <input type="file" accept="image/*" @change="onAvatarChange" class="file-input" />
                  <span class="material-symbols-rounded">photo_camera</span>
                </label>
              </div>
            </div>

            <!-- Full Name -->
            <div class="input-group">
              <label class="input-label">
                <span class="material-symbols-rounded">badge</span>
                Nombre completo
              </label>
              <div class="input-wrapper" :class="{ 'input-error-wrapper': errors.full_name }">
                <span class="input-icon">
                  <span class="material-symbols-rounded">badge</span>
                </span>
                <input v-model="full_name" type="text" placeholder="Tu nombre completo" class="input-field"
                  @blur="validateField('full_name')" @input="clearFieldError('full_name')" />
              </div>
              <Transition name="slide-fade">
                <small v-if="errors.full_name" class="error-text">
                  <span class="material-symbols-rounded">error</span>
                  {{ errors.full_name }}
                </small>
              </Transition>
            </div>

            <!-- Username -->
            <div class="input-group">
              <label class="input-label">
                <span class="material-symbols-rounded">person</span>
                Usuario
              </label>
              <div class="input-wrapper" :class="{ 'input-error-wrapper': errors.username }">
                <span class="input-icon">
                  <span class="material-symbols-rounded">person</span>
                </span>
                <input v-model="username" type="text" placeholder="usuario" class="input-field"
                  @blur="validateField('username')" @input="clearFieldError('username'); usernameSuggestions = []" />
              </div>
              <Transition name="slide-fade">
                <small v-if="errors.username" class="error-text">
                  <span class="material-symbols-rounded">error</span>
                  {{ errors.username }}
                </small>
              </Transition>

              <!-- Username Suggestions -->
              <Transition name="slide-fade">
                <div v-if="usernameSuggestions.length > 0" class="suggestions-container">
                  <small class="suggestions-label">
                    <span class="material-symbols-rounded">lightbulb</span>
                    Prueba con alguno de estos:
                  </small>
                  <div class="suggestions-list">
                    <button v-for="suggestion in usernameSuggestions" :key="suggestion" type="button"
                      class="suggestion-chip" @click="selectSuggestion(suggestion)">
                      {{ suggestion }}
                    </button>
                  </div>
                </div>
              </Transition>
            </div>


            <!-- Email -->
            <div class="input-group">
              <label class="input-label">
                <span class="material-symbols-rounded">email</span>
                Email
              </label>
              <div class="input-wrapper" :class="{ 'input-error-wrapper': errors.email }">
                <span class="input-icon">
                  <span class="material-symbols-rounded">email</span>
                </span>
                <input v-model="email" type="email" placeholder="email@dominio.com" class="input-field"
                  @blur="validateField('email')" @input="clearFieldError('email')" />
              </div>
              <Transition name="slide-fade">
                <small v-if="errors.email" class="error-text">
                  <span class="material-symbols-rounded">error</span>
                  {{ errors.email }}
                </small>
              </Transition>
            </div>

            <!-- Password -->
            <div class="input-group">
              <label class="input-label">
                <span class="material-symbols-rounded">lock</span>
                Contraseña
              </label>
              <div class="input-wrapper password-wrapper" :class="{ 'input-error-wrapper': errors.password }">
                <span class="input-icon">
                  <span class="material-symbols-rounded">lock</span>
                </span>
                <input v-model="password" :type="showPassword ? 'text' : 'password'" placeholder="Mínimo 10 caracteres"
                  class="input-field" @blur="validateField('password')" @input="clearFieldError('password')" />
                <button type="button" class="toggle-password" @click="showPassword = !showPassword" tabindex="-1">
                  <span class="material-symbols-rounded">
                    {{ showPassword ? 'visibility_off' : 'visibility' }}
                  </span>
                </button>
              </div>
              <Transition name="slide-fade">
                <small v-if="errors.password" class="error-text">
                  <span class="material-symbols-rounded">error</span>
                  {{ errors.password }}
                </small>
              </Transition>

              <!-- Password Strength -->
              <Transition name="slide-fade">
                <div v-if="password && !errors.password" class="password-strength">
                  <div class="strength-bar">
                    <div class="strength-fill" :style="{
                      width: `${(passwordStrength.level / 5) * 100}%`,
                      backgroundColor: passwordStrength.color
                    }"></div>
                  </div>
                  <span class="strength-label" :style="{ color: passwordStrength.color }">
                    <span class="material-symbols-rounded" style="font-size: 1rem;">{{ passwordStrength.level >= 4 ?
                      'verified' : 'lock' }}</span>
                    {{ passwordStrength.label }}
                  </span>
                </div>
              </Transition>
            </div>

            <!-- Submit Button -->
            <button type="submit" class="submit-btn" :disabled="loading">
              <span v-if="!loading" class="btn-content">
                <span>REGISTRARSE</span>
                <span class="material-symbols-rounded">person_add</span>
              </span>
              <span v-else class="btn-content">
                <span class="loading-spinner"></span>
                <span>Registrando...</span>
              </span>
            </button>

            <!-- Sign In Link -->
            <div class="form-links">
              <router-link to="/login" class="link-item">
                <span class="material-symbols-rounded">login</span>
                <span>¿Ya tienes cuenta? Iniciar sesión</span>
              </router-link>
            </div>
          </form>
        </div>
      </div>

      <!-- Right Side - Image -->
      <div class="illustration-side">
        <div class="image-container">
          <img src="/img/sushi_1.png" alt="Sushito" class="register-image" />
          <div class="image-overlay"></div>
        </div>
      </div>
    </div>
  </main>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap');
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800;900&family=Cinzel:wght@600;700;800&display=swap');

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.register-container {
  display: flex;
  min-height: 100vh;
  align-items: center;
  justify-content: center;
  font-family: 'Poppins', sans-serif;
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  padding: 1.5rem;
  position: relative;
  overflow: hidden;
}

.register-wrapper {
  max-width: 1000px;
  width: 100%;
  display: flex;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.5);
  border-radius: 20px;
  overflow: hidden;
  background: rgba(20, 24, 30, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(198, 146, 61, 0.2);
  animation: slideUp 0.6s ease-out;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ===== LEFT SIDE - FORM ===== */
.form-side {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 2rem;
  background: transparent;
  overflow: hidden;
}

.form-content {
  width: 100%;
  max-width: 380px;
  animation: fadeIn 0.8s ease-out 0.2s both;
}

.form-content::-webkit-scrollbar {
  width: 4px;
}

.form-content::-webkit-scrollbar-track {
  background: transparent;
}

.form-content::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 2px;
}

.form-content::-webkit-scrollbar-thumb:hover {
  background: rgba(0, 0, 0, 0.2);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateX(-20px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.header {
  margin-bottom: 1.2rem;
  position: relative;
  text-align: center;
}

.back-to-landing {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  margin-bottom: 1.5rem;
  background: rgba(198, 146, 61, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(198, 146, 61, 0.3);
  border-radius: 12px;
  color: #C6923D;
  text-decoration: none;
  font-size: 0.9rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.back-to-landing:hover {
  background: rgba(198, 146, 61, 0.15);
  border-color: rgba(198, 146, 61, 0.5);
  transform: translateX(-4px);
  box-shadow: 0 4px 12px rgba(198, 146, 61, 0.3);
}

.back-to-landing .material-symbols-rounded {
  font-size: 1.2rem;
}

.welcome-text {
  font-size: 0.9rem;
  font-weight: 500;
  color: #64748b;
  margin-bottom: 0.2rem;
  letter-spacing: 0.5px;
}

.brand-name {
  font-size: 1.8rem;
  font-weight: 900;
  font-family: 'Playfair Display', 'Cinzel', 'Times New Roman', serif;
  text-transform: uppercase;
  background: linear-gradient(135deg,
      #FFD700 0%,
      #FFE55C 10%,
      #FFD700 20%,
      #F4D03F 35%,
      #FFD700 45%,
      #C6923D 60%,
      #9D6B27 75%,
      #C6923D 85%,
      #FFD700 95%,
      #FFE55C 100%);
  background-size: 300% 300%;
  animation: shimmer 2.5s ease-in-out infinite;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  letter-spacing: 0.2em;
  margin: 0 0 0.3rem 0;
  text-shadow:
    3px 4px 0px rgba(0, 0, 0, 1),
    5px 7px 12px rgba(0, 0, 0, 0.9),
    7px 10px 20px rgba(0, 0, 0, 0.8),
    0px 2px 4px rgba(255, 215, 0, 0.6),
    0px -2px 2px rgba(114, 68, 16, 0.8),
    inset 0px 1px 0px rgba(255, 255, 255, 0.3);
  filter:
    drop-shadow(3px 5px 10px rgba(0, 0, 0, 0.9)) drop-shadow(1px 2px 4px rgba(255, 215, 0, 0.4));
  position: relative;
  transform: perspective(100px) rotateX(2deg);
}

@keyframes shimmer {

  0%,
  100% {
    background-position: 0% 50%;
  }

  50% {
    background-position: 100% 50%;
  }
}

.register-form {
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
}

.error-message {
  padding: 0.85rem 1rem;
  background: linear-gradient(135deg, rgba(255, 107, 107, 0.15) 0%, rgba(255, 107, 107, 0.1) 100%);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 107, 107, 0.3);
  border-left: 4px solid #ff6b6b;
  color: #ff6b6b;
  font-size: 0.85rem;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.1);
}

.error-message .material-symbols-rounded {
  font-size: 1.2rem;
}

/* Avatar Upload */
.avatar-upload {
  display: flex;
  justify-content: center;
  margin-bottom: 0.3rem;
}

.avatar-container {
  position: relative;
  display: inline-block;
}

.avatar-preview {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(198, 146, 61, 0.3);
  box-shadow: 0 4px 12px rgba(198, 146, 61, 0.3);
  transition: all 0.3s ease;
}

.avatar-preview:hover {
  transform: scale(1.05);
  box-shadow: 0 12px 32px rgba(198, 146, 61, 0.4);
}

.upload-label {
  position: absolute;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  border-radius: 50%;
  cursor: pointer;
  transition: all 0.3s;
  color: #000000;
  box-shadow: 0 4px 12px rgba(198, 146, 61, 0.4);
  border: 3px solid white;
}

.upload-label .material-symbols-rounded {
  font-size: 1.1rem;
}

.upload-label:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(198, 146, 61, 0.5);
}

.file-input {
  display: none;
}

/* Input Groups */
.input-group {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.input-label {
  font-size: 0.85rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.input-label .material-symbols-rounded {
  font-size: 1.1rem;
  color: #667eea;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 2px solid rgba(198, 146, 61, 0.2);
  border-radius: 14px;
  transition: all 0.3s ease;
  overflow: hidden;
}

.input-wrapper:focus-within {
  border-color: #C6923D;
  box-shadow: 0 0 0 4px rgba(198, 146, 61, 0.15),
    0 4px 12px rgba(198, 146, 61, 0.2);
  transform: translateY(-2px);
}

.input-error-wrapper {
  border-color: #ff6b6b !important;
  box-shadow: 0 0 0 4px rgba(255, 107, 107, 0.1) !important;
}

.input-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 1rem;
  color: #C6923D;
  pointer-events: none;
}

.input-icon .material-symbols-rounded {
  font-size: 1.3rem;
}

.input-field {
  flex: 1;
  padding: 0.75rem 1rem 0.75rem 0;
  border: none;
  background: transparent;
  font-size: 0.9rem;
  font-family: 'Poppins', sans-serif;
  color: #ffffff;
  transition: all 0.3s;
  outline: none;
}

.input-field::placeholder {
  color: rgba(255, 255, 255, 0.5);
  font-weight: 400;
}

.error-text {
  font-size: 0.8rem;
  color: #ff6b6b;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: 500;
  margin-top: -0.3rem;
}

.error-text .material-symbols-rounded {
  font-size: 1rem;
}

/* Username Suggestions */
.suggestions-container {
  margin-top: 0.5rem;
  padding: 0.75rem;
  background: rgba(198, 146, 61, 0.1);
  border: 1px solid rgba(198, 146, 61, 0.3);
  border-radius: 12px;
}

.suggestions-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #C6923D;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}

.suggestions-label .material-symbols-rounded {
  font-size: 1rem;
}

.suggestions-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.suggestion-chip {
  padding: 0.4rem 0.8rem;
  background: rgba(198, 146, 61, 0.2);
  border: 1px solid rgba(198, 146, 61, 0.4);
  border-radius: 20px;
  color: #DFC05E;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: 'Poppins', sans-serif;
}

.suggestion-chip:hover {
  background: rgba(198, 146, 61, 0.35);
  border-color: #C6923D;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(198, 146, 61, 0.3);
}

.suggestion-chip:active {
  transform: translateY(0);
}


/* Password Wrapper */
.password-wrapper {
  position: relative;
}

.password-wrapper .input-icon {
  padding-right: 0.5rem;
}

.toggle-password {
  position: absolute;
  right: 0.75rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
  border-radius: 8px;
}

.toggle-password:hover {
  color: #C6923D;
  background: rgba(198, 146, 61, 0.15);
}

.toggle-password:active {
  transform: scale(0.95);
}

.toggle-password .material-symbols-rounded {
  font-size: 1.3rem;
}

/* Password Strength */
.password-strength {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-top: 0.3rem;
  padding: 0.5rem 0.7rem;
  background: rgba(247, 237, 125, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 8px;
  border: 1px solid rgba(198, 146, 61, 0.2);
}

.strength-bar {
  flex: 1;
  height: 6px;
  background: rgba(148, 163, 184, 0.2);
  border-radius: 10px;
  overflow: hidden;
}

.strength-fill {
  height: 100%;
  transition: all 0.4s ease;
  border-radius: 10px;
  box-shadow: 0 0 8px currentColor;
}

.strength-label {
  font-size: 0.75rem;
  font-weight: 700;
  min-width: 80px;
  text-align: right;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

/* Submit Button */
.submit-btn {
  width: 100%;
  padding: 0.85rem 1.5rem;
  background: linear-gradient(135deg, #C6923D 0%, #DFC05E 100%);
  color: #000000;
  border: none;
  border-radius: 14px;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 0.3rem;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(198, 146, 61, 0.4);
}

.submit-btn::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #DFC05E 0%, #F7ED7D 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.submit-btn:hover:not(:disabled)::before {
  opacity: 1;
}

.submit-btn:hover:not(:disabled) {
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(198, 146, 61, 0.5);
}

.submit-btn:active:not(:disabled) {
  transform: translateY(-1px);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.btn-content {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
}

.btn-content .material-symbols-rounded {
  font-size: 1.2rem;
  transition: transform 0.3s ease;
}

.submit-btn:hover:not(:disabled) .btn-content .material-symbols-rounded {
  transform: scale(1.1);
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.loading-spinner {
  display: inline-block;
  width: 20px;
  height: 20px;
  border: 3px solid rgba(0, 0, 0, 0.3);
  border-top-color: #000000;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

/* Links */
.form-links {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  margin-top: 0.8rem;
}

.link-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  padding: 0.75rem 1rem;
  background: rgba(198, 146, 61, 0.08);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(198, 146, 61, 0.2);
  border-radius: 12px;
  color: #C6923D;
  text-decoration: none;
  font-size: 0.85rem;
  font-weight: 600;
  transition: all 0.3s ease;
}

.link-item:hover {
  background: rgba(198, 146, 61, 0.15);
  border-color: rgba(198, 146, 61, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(198, 146, 61, 0.3);
}

.link-item .material-symbols-rounded {
  font-size: 1.1rem;
}

/* ===== RIGHT SIDE - IMAGE ===== */
.illustration-side {
  flex: 1;
  position: relative;
  overflow: hidden;
  background: #000;
}

.image-container {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 500px;
}

.register-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  transition: transform 0.6s ease;
}

.register-wrapper:hover .register-image {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.2) 0%, rgba(223, 192, 94, 0.25) 100%);
  pointer-events: none;
}

/* Transitions */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}

.slide-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* Responsive */
@media (max-width: 968px) {
  .register-container {
    min-height: 100vh;
    height: auto;
    padding: 0;
    align-items: flex-start;
  }

  .register-wrapper {
    flex-direction: column;
    max-width: 100%;
    width: 100%;
    min-height: 100vh;
    border-radius: 0;
    overflow-y: auto;
  }

  .illustration-side {
    min-height: 180px;
    max-height: 180px;
    order: -1;
    flex-shrink: 0;
  }

  .image-container {
    min-height: 180px;
    max-height: 180px;
  }

  .form-side {
    padding: 1.5rem 1.5rem;
    padding-bottom: 3rem;
    flex: 1;
    overflow: visible;
    max-height: none;
  }

  .form-content {
    max-width: 100%;
    overflow: visible;
  }
}

@media (max-width: 480px) {
  .register-container {
    padding: 0;
  }

  .register-wrapper {
    border-radius: 0;
  }

  .illustration-side {
    min-height: 150px;
    max-height: 150px;
  }

  .image-container {
    min-height: 150px;
    max-height: 150px;
  }

  .form-side {
    padding: 1.25rem 1.25rem;
    padding-bottom: 2.5rem;
  }

  .welcome-text {
    font-size: 0.85rem;
  }

  .brand-name {
    font-size: 1.4rem;
  }

  .input-wrapper {
    border-radius: 12px;
  }

  .submit-btn {
    padding: 0.85rem 1.25rem;
    font-size: 0.9rem;
  }

  .avatar-preview {
    width: 65px;
    height: 65px;
  }

  .back-to-landing {
    padding: 0.5rem 1rem;
    margin-bottom: 0.75rem;
    font-size: 0.8rem;
  }

  .header {
    margin-bottom: 0.8rem;
  }

  .input-group {
    gap: 0.35rem;
  }

  .register-form {
    gap: 0.7rem;
  }

  .form-links {
    margin-top: 0.75rem;
    padding-bottom: 1rem;
  }

  .avatar-upload {
    margin-bottom: 0.2rem;
  }
}

/* ===== CUSTOM MODAL ===== */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
}

.modal-container {
  background: rgba(20, 24, 30, 0.98);
  border: 1px solid rgba(198, 146, 61, 0.3);
  border-radius: 20px;
  padding: 2rem;
  max-width: 400px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.modal-icon {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: iconPop 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
}

@keyframes iconPop {
  0% {
    transform: scale(0);
    opacity: 0;
  }

  50% {
    transform: scale(1.1);
  }

  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.modal-icon.error {
  background: linear-gradient(135deg, #ff6b6b, #ee5a6f);
}

.modal-icon.success {
  background: linear-gradient(135deg, #51cf66, #00C2A8);
}

.modal-icon .material-symbols-rounded {
  font-size: 3rem;
  color: white;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  text-align: center;
}

.modal-message {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.8);
  text-align: center;
  line-height: 1.5;
  margin: 0;
}

.modal-btn {
  width: 100%;
  padding: 0.75rem;
  background: linear-gradient(135deg, #C6923D, #DFC05E);
  color: #000000;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  margin-top: 0.5rem;
}

.modal-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(198, 146, 61, 0.4);
}

/* Modal Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
  transition: transform 0.3s ease;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
  transform: scale(0.9) translateY(-20px);
}
</style>